import React, { useEffect } from 'react';
import { CvrField } from '@flos/react-ui';
import useAppState from '../../state/appState';
import { Cvrfield as CvrfieldType, FormValues, FormConfig } from '@topdanmark-onlinesolutions/claim-form-types';
import { getIsValid, getValidationErrors } from '../../validators/validator-mapping';
import { ElementValue } from '../../types/form-values';
import { ClaimFormSubmitFieldValue } from '../../types/claim-form-submit-field-value';
import { SummaryRowTemplate } from '../../helpers/templates';
import { ElementValueHuman } from '../../types/element-values';
import { isRequired } from '../../helpers/isRequired';
import { getValue } from '../values/Get';
import { getElementFromElementId } from '../../helpers/getElementFromElementId';
import { getClaimFormSubmitValue } from '../../helpers/getClaimFormSubmitValue';

const Cvrfield = ({ elementId, caption, placeholder, helpText }: CvrfieldType) => {
    const formConfig = useAppState((state) => state.formConfig);
    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);

    const value = Cvrfield.getValueOrDefault(formValues, elementId);

    const [errorMessage, setErrorMessage] = React.useState('');

    const [fieldHasHadFocusAtLeastOnce, setFieldHasHadFocusAtLeastOnce] = React.useState(false);

    const doValidation = () => {
        if (!formConfig) {
            return;
        }

        if (!fieldHasHadFocusAtLeastOnce) {
            return;
        }

        const errorMessages = getValidationErrors(formConfig, formValues, elementId);
        setErrorMessage(errorMessages[0]);
    };

    useEffect(() => {
        doValidation();
        // eslint-disable-next-line
    }, [value]);

    const isRequiredField = isRequired(formConfig, elementId);

    const isValid = Cvrfield.isValidField(formConfig, formValues, elementId, fieldHasHadFocusAtLeastOnce);

    return (
        <>
            {caption && <h6>{caption}</h6>}
            <CvrField
                value={value}
                onBlur={() => {
                    doValidation();
                }}
                onFocus={() => {
                    setFieldHasHadFocusAtLeastOnce(true);
                }}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const value = event.target.value;
                    setFormValue(elementId, value);
                }}
                isValid={isValid}
                errorText={errorMessage}
                id={elementId}
                key={elementId}
                label={placeholder}
                helpText={helpText}
                required={isRequiredField}
                aria-label={caption}
                data-testid="cvrfield"
            />
        </>
    );
};

Cvrfield.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string): JSX.Element => {
    const element = getElementFromElementId(formConfig, elementId) as CvrfieldType;
    const { summaryCaption } = element;

    const value = getValue(formConfig, formValues, elementId);

    // Happens when the user has not filled
    // out previous pages but somehow navigated to the resume page
    if (value === undefined) {
        return <></>;
    }

    return <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={value} />;
};

Cvrfield.getValueSubmit = (formValues: FormValues, elementId: string, label: string): ClaimFormSubmitFieldValue => {
    const valueObj = Cvrfield.getValue(formValues, elementId);
    const valueStr = Cvrfield.getValueHuman(formValues, elementId);

    const valueId = { string: valueObj };

    return getClaimFormSubmitValue('cvrfield', elementId, label, valueStr, valueId);
};

Cvrfield.getValue = (formValues: FormValues, elementId: string): ElementValue => {
    return formValues[elementId];
};

Cvrfield.getValueOrDefault = (formValues: FormValues, elementId: string) => {
    const value = Cvrfield.getValue(formValues, elementId);
    if (value === undefined) {
        return '';
    }
    return value;
};

Cvrfield.getValueHuman = (formValues: FormValues, elementId: string): ElementValueHuman => {
    const valueObj = Cvrfield.getValue(formValues, elementId);

    const valueStr = valueObj;

    return valueStr;
};

Cvrfield.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string): boolean | undefined => {
    return getIsValid(formConfig, formValues, elementId);
};

Cvrfield.isValidField = (
    formConfig: FormConfig | undefined,
    formValues: FormValues,
    elementId: string,
    fieldHadFocusAtLeastOnce: boolean
): boolean | undefined => {
    if (!formConfig) {
        return undefined;
    }

    if (!fieldHadFocusAtLeastOnce) {
        return undefined;
    }

    const isValid = Cvrfield.isValid(formConfig, formValues, elementId);

    return isValid;
};

export default Cvrfield;
